'use client'

import type { PublicPatient } from '@snootsvet/shared/portal'

import { PetSelect } from '@/customer/components/pet/pet-select'
import {
  type PetTileColor,
  PetTileContent,
  type PetTileMonogramColor,
  petTileStyles,
} from '@/customer/components/pet/pet-tile'
import { RadioGroup, RadioGroupItem } from '@/customer/components/ui/radio-group'
import { useIsMobile } from '@/customer/hooks/use-is-mobile'
import { cn } from '@/customer/lib/utils'

/** Back-compat alias so existing callers keep working. */
export type PetPickerColor = PetTileColor

type PetPickerProps = {
  /** Selected pet id, or `null` when nothing is picked yet. */
  value: string | null
  onValueChange: (id: string) => void
  pets: PublicPatient[]
  /**
   * Accent colour used for hover tint + selected fill on the tile.
   * Defaults to `yellow` to match the routine-visit booking dialog.
   */
  color?: PetTileColor
  /**
   * Fill colour of the species-icon monogram tile. Kept separate
   * from `color` so the monogram can pop against the accent
   * (default `pink` on a `yellow` tile).
   */
  monogramColor?: PetTileMonogramColor
  /**
   * Return `true` to render a pet as disabled - useful for pets
   * without an active plan, without a linked clinic, etc.
   */
  isPetDisabled?: (pet: PublicPatient) => boolean
  className?: string
  'aria-labelledby'?: string
}

/**
 * Choose-one picker for pets. Sibling to `CardRadioGroup`: same
 * underlying `RadioGroup` primitive and same border/checked
 * vocabulary. Shares its tile visuals with the pets-page tab list
 * via `PetTile`.
 *
 * Layout defaults to a responsive grid; override via `className` for
 * dense/narrow surfaces.
 */
export function PetPicker({
  value,
  onValueChange,
  pets,
  color = 'yellow',
  monogramColor = 'pink',
  isPetDisabled,
  className,
  'aria-labelledby': ariaLabelledBy,
}: PetPickerProps) {
  const isMobile = useIsMobile()

  // Wait for the media query to resolve before mounting either
  // surface - avoids a wrong-layout flash on cold load, matching
  // `WeekSlotPicker`.
  if (isMobile === undefined) return null

  if (isMobile) {
    return (
      <PetSelect
        value={value}
        onValueChange={onValueChange}
        pets={pets}
        className={className}
        aria-labelledby={ariaLabelledBy}
      />
    )
  }

  return (
    <RadioGroup
      value={value}
      onValueChange={(v) => onValueChange(v as string)}
      className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', className)}
      aria-labelledby={ariaLabelledBy}
    >
      {pets.map((pet) => (
        <RadioGroupItem
          key={pet.id}
          value={pet.id}
          disabled={isPetDisabled?.(pet) ?? false}
          className={petTileStyles({ color })}
        >
          <PetTileContent pet={pet} isSelected={value === pet.id} monogramColor={monogramColor} />
        </RadioGroupItem>
      ))}
    </RadioGroup>
  )
}
